/**
 * Google Analytics 4 tracking utilities
 */

import { getUTMForGA4 } from './utmCapture'

interface GAEventParams {
  value?: number
  currency?: string
  transaction_id?: string
  items?: Array<{
    item_id: string
    item_name: string
    price: number
    quantity?: number
  }>
  [key: string]: unknown
}

/**
 * Track Google Analytics event
 */
export const trackGAEvent = (eventName: string, params?: GAEventParams): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params)

    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log('[GA4]', eventName, params)
    }
  }
}

/**
 * Track "View Item" - when user views pricing page
 */
export const trackGAViewPricing = (source: string, feature?: string): void => {
  trackGAEvent('view_item_list', {
    item_list_name: 'Pricing Plans',
    items: [
      {
        item_id: 'pro-monthly',
        item_name: 'Pro Subscription - Monthly',
        price: 7,
      },
      {
        item_id: 'pro-quarterly',
        item_name: 'Pro Subscription - Quarterly',
        price: 17,
      },
      {
        item_id: 'pro-lifetime',
        item_name: 'Pro Subscription - Lifetime',
        price: 99,
      }
    ],
    source,
    feature,
    ...getUTMForGA4(),
  })
}

/**
 * Track "Begin Checkout" - when user clicks Subscribe button on pricing page
 */
export const trackGABeginCheckout = (
  priceType: 'monthly' | 'quarterly' | 'lifetime',
  amount: number
): void => {
  const itemName = `Pro Subscription - ${priceType.charAt(0).toUpperCase() + priceType.slice(1)}`
  
  trackGAEvent('begin_checkout', {
    value: amount,
    currency: 'USD',
    items: [
      {
        item_id: `pro-${priceType}`,
        item_name: itemName,
        price: amount,
        quantity: 1,
      }
    ],
    ...getUTMForGA4(), // Include UTM attribution
  })
}

/**
 * Track successful purchase/subscription
 * This is the key conversion event for Google Analytics
 */
export const trackGAPurchase = (
  priceType: 'monthly' | 'quarterly' | 'lifetime',
  amount: number,
  transactionId?: string
): void => {
  const itemName = `Pro Subscription - ${priceType.charAt(0).toUpperCase() + priceType.slice(1)}`
  
  trackGAEvent('purchase', {
    value: amount,
    currency: 'USD',
    transaction_id: transactionId || `sub-${Date.now()}`,
    items: [
      {
        item_id: `pro-${priceType}`,
        item_name: itemName,
        price: amount,
        quantity: 1,
      }
    ],
    ...getUTMForGA4(), // Include UTM attribution
  })
}

/**
 * Track user registration - matches FB CompleteRegistration event
 */
export const trackGASignUp = (
  method: 'email_otp' | 'google' | 'facebook' | 'apple'
): void => {
  trackGAEvent('sign_up', {
    method,
    ...getUTMForGA4(),
  })
}

/**
 * Track contact attempt - matches FB Contact event
 */
export const trackGAContact = (source: string = 'footer'): void => {
  trackGAEvent('contact_click', {
    source,
    ...getUTMForGA4(),
  })
}

/**
 * Track AI Chat session start
 */
export const trackGAAIChatStart = (applicationId: string, hasJobContent: boolean): void => {
  trackGAEvent('ai_chat_start', {
    application_id: applicationId,
    has_job_content: hasJobContent,
    ...getUTMForGA4(),
  })
}

/**
 * Track AI Chat message sent
 */
export const trackGAAIChatMessage = (applicationId: string, messageCount: number): void => {
  trackGAEvent('ai_chat_message', {
    application_id: applicationId,
    message_count: messageCount,
    ...getUTMForGA4(),
  })
}

/**
 * Track application added - key activation event
 * @param isFirst - Whether this is the user's first application (activation)
 * @param totalCount - Total number of applications after adding this one
 */
export const trackGAApplicationAdded = (isFirst: boolean, totalCount: number): void => {
  trackGAEvent('application_added', {
    is_first: isFirst,
    total_count: totalCount,
    ...getUTMForGA4(), // Include UTM attribution
  })

  // Also track as key activation event if it's the first application
  if (isFirst) {
    trackGAEvent('activation', {
      method: 'first_application',
      ...getUTMForGA4(),
    })
  }
}

/**
 * Set GA4 user properties for audience segmentation
 * Call this after user authentication and plan load
 * @param userId - User's unique ID (Supabase UUID)
 * @param planType - User's subscription plan ('free' | 'pro')
 * @param appCount - Number of applications the user has
 */
export const setGAUserId = (userId: string | null): void => {
  if (typeof window !== 'undefined') window.gtag?.('set', { user_id: userId })
}

export const setGAUserProperties = (
  userId: string,
  planType: 'free' | 'pro',
  appCount: number
): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Calculate app count bucket for segmentation
    let appCountBucket: string
    if (appCount === 0) appCountBucket = '0'
    else if (appCount <= 5) appCountBucket = '1-5'
    else if (appCount <= 10) appCountBucket = '6-10'
    else if (appCount <= 25) appCountBucket = '11-25'
    else if (appCount <= 50) appCountBucket = '26-50'
    else appCountBucket = '50+'

    // User-ID is a reserved configuration parameter, not a custom user property.
    setGAUserId(userId)
    window.gtag('set', 'user_properties', {
      plan_type: planType,
      app_count_bucket: appCountBucket,
    })

    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log('[GA4] User properties set:', {
        user_id: userId,
        plan_type: planType,
        app_count_bucket: appCountBucket,
      })
    }
  }
}

/**
 * Track resume builder: a new resume was created
 */
export const trackGAResumeCreated = (
  source: 'blank' | 'import_analysis' | 'duplicate' | 'tailor',
  template?: string
): void => {
  trackGAEvent('resume_created', {
    source,
    template,
    ...getUTMForGA4(),
  })
}

/**
 * Track resume builder: PDF download
 */
export const trackGAResumeDownloaded = (template: string, font?: string): void => {
  trackGAEvent('resume_downloaded', {
    template,
    font,
    ...getUTMForGA4(),
  })
}

/**
 * Track resume builder: AI tailor invoked
 */
export const trackGAResumeTailored = (applicationId: string): void => {
  trackGAEvent('resume_tailored', {
    application_id: applicationId,
    ...getUTMForGA4(),
  })
}

/**
 * Track resume builder: template changed
 */
export const trackGAResumeTemplateChanged = (template: string): void => {
  trackGAEvent('resume_template_changed', {
    template,
    ...getUTMForGA4(),
  })
}
